import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { RoutePath } from '@components/AppRouter/constants';
import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { objectFieldsToSnakeCase } from '@src/utils/objectFieldsToCamelCase';
import { constantStore } from '@stores/constantStore';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { ActionButton } from './ActionButton';
import { columns } from './columns';

import styles from './Table.module.scss';

const { TABLE_NAMES } = constantStore;

export const Table = observer(() => {
  const { breadcrumbs, selectNode, controls, setChecked } = controlsStore;
  const { push } = useHistory();

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_CONTROLS });

  useEffect(() => {
    update();
  }, [selectNode, update]);

  const hardFilter = useMemo(() => {
    if (selectNode) {
      const out = [
        {
          name: 'rep_group_code',
          type: FILTER.EQ,
          values: [selectNode.repGroupCode ?? ''],
          filterIsInverted: false,
        },
      ];

      if (selectNode.ctrlGroupCode)
        out.push({
          name: 'ctrl_group_code',
          type: FILTER.EQ,
          values: [selectNode.ctrlGroupCode ?? ''],
          filterIsInverted: false,
        });

      return [out];
    }

    return undefined;
  }, [selectNode]);

  const localData = useMemo(() => controls.map((item) => objectFieldsToSnakeCase(item)), [controls]);

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      // TODO переделать когда, в таблицном компоненте на беке добавят запрос для получение выбранных строк
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: { ...tr, hardFilter },
        localData: {
          data: localData,
          fieldFormat: {},
        },
        selectRows: sr,
        keyField: 'ctrl_code',
      });

      setChecked(rows);
    },
    [hardFilter, localData, setChecked],
  );

  const onRowClick = useCallback(
    (value) => {
      push(`${RoutePath.DICTIONARY_CONTROLS}/${value?.ctrl_code}`);
    },
    [push],
  );

  const title = useMemo(
    () => (
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((value) => (
          <div key={value}>{value}</div>
        ))}
      </div>
    ),
    [breadcrumbs],
  );

  const [titleWithCount, onTableChange] = useTableTitleWithCount(title);

  if (!selectNode) return null;

  return (
    <div className={styles.table}>
      <KSNMTable
        externalControl={externalControl}
        tableName={TABLE_NAMES.DICTIONARY_CONTROLS}
        columns={columns}
        tableTitle={titleWithCount}
        resource="ctrl.v_nsi_ctrl"
        externalFetch={ksnmTableStore.externalFetch}
        externalFetchXLSX={getExcelWithAudit('Справочник "Контроли"')}
        hardFilter={hardFilter}
        onRowClick={onRowClick}
        onCheckBoxChange={onCheckBoxChange}
        extendTableButtons={<ActionButton update={update} />}
        showAggregateRow={false}
        key={key}
        keyField="ctrl_code"
        onTableChange={onTableChange}
      />
    </div>
  );
});
